import React, { useState, useContext, useEffect, useRef } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  MenuItem,
  IconButton,
  Menu,
  Switch,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  InputAdornment,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import AccountCircle from "@material-ui/icons/AccountCircle";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import CheckCircle from "@material-ui/icons/CheckCircle";
import RadioButtonUnchecked from "@material-ui/icons/RadioButtonUnchecked";
import { toast } from "react-toastify";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import { useBranding } from "../context/Branding";
import api from "../services/api";
import toastError from "../errors/toastError";
import { getBackendUrl } from "../config";
import MobileNavigation from "../components/MobileNavigation";
import PendingAnnouncementsAlert from "../components/PendingAnnouncementsAlert";
import riosaudeLogo from "../assets/riosaude-logo.png";
import rsdeskIcon from "../assets/rsdesk-icon.png";

/*
 * Largura da sidebar. 268 era generoso demais em notebook: com
 * escalonamento do Windows a 125%, ela comia quase um quarto da tela.
 */
const drawerWidth = 232;
const drawerClosedWidth = 76;
const SIDEBAR_STORAGE_KEY = "rsdeskSidebarOpen";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    background: theme.palette.background.default,
    [theme.breakpoints.down("sm")]: {
      height: "100vh",
    },
  },
  /*
   * Barra superior: e navegacao, nao conteudo.
   * =============================================================
   * ATENCAO: as regras de cada seletor precisam ficar em UM unico
   * bloco. Havia dois "& .MuiSvgIcon-root" aqui e o segundo apagava o
   * primeiro — era por isso que o tamanho dos icones nao pegava.
   * =============================================================
   */
  toolbar: {
    paddingRight: theme.spacing(2),
    minHeight: 48,
    gap: theme.spacing(0.25),
    color: theme.palette.text.primary,
    "& .MuiIconButton-root": {
      padding: theme.spacing(0.75),
      color: theme.palette.text.primary,
    },
    "& .MuiSvgIcon-root": {
      fontSize: 19,
      color: `${theme.palette.text.primary} !important`,
      fill: "currentColor !important",
    },
    "& .MuiTypography-root": {
      color: theme.palette.text.primary,
    },
    [theme.breakpoints.down("xs")]: {
      minHeight: 44,
      paddingRight: theme.spacing(0.5),
    },
  },
  // Alinhado com a barra superior (52), para que a logo e o conteudo
  // comecem na mesma linha em vez de criar dois degraus.
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1, 0.5, 1.5),
    minHeight: 48,
    background: theme.custom.sidebarStrong,
    overflow: "hidden",
  },
  toolbarIconClosed: {
    justifyContent: "center",
    minHeight: 48,
    padding: theme.spacing(0.5, 0),
  },
  sidebarBrand: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    gap: theme.spacing(1),
    flex: 1,
  },
  // Logo institucional na versao branca sem fundo: o proprio navy
  // do cabecalho da sidebar serve de fundo.
  sidebarLogoFrame: {
    width: 150,
    height: 34,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flexShrink: 0,
  },
  sidebarLogo: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    objectPosition: "left center",
  },
  sidebarIconFrame: {
    width: 40,
    height: 40,
    borderRadius: theme.custom.radius.sm,
    background: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 4,
    boxShadow: "0 6px 16px rgba(2, 6, 11, 0.45)",
  },
  sidebarIconLogo: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
  },
  sidebarBrandFallback: {
    width: 48,
    height: 48,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    color: "#FFFFFF",
    background: `linear-gradient(135deg, ${theme.custom.sidebarSoft} 0%, ${theme.palette.secondary.main} 100%)`,
  },
  collapseButton: {
    color: "#FFFFFF",
    background: "rgba(255,255,255,0.08)",
    "&:hover": {
      background: "rgba(255,255,255,0.14)",
    },
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  appBarShiftCollapsed: {
    marginLeft: drawerClosedWidth,
    width: `calc(100% - ${drawerClosedWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarMobile: {
    marginLeft: 0,
    width: "100%",
  },
  menuButton: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    color: theme.palette.text.primary,
    fontWeight: 800,
    letterSpacing: 0,
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    overflowY: "auto",
    scrollbarWidth: "thin",
    scrollbarColor: `rgba(148, 163, 184, 0.58) ${theme.custom.sidebar}`,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: theme.custom.sidebar,
    borderRight: "1px solid rgba(148, 163, 184, 0.16)",
    color: "#FFFFFF",
    "& .MuiListItemIcon-root": {
      color: "#FFFFFF !important",
    },
    "& .MuiSvgIcon-root": {
      color: "#FFFFFF !important",
      fill: "#FFFFFF !important",
    },
    "& .MuiListItemText-primary": {
      color: "#FFFFFF",
    },
    "& .MuiTypography-root": {
      color: "#FFFFFF",
    },
    "&::-webkit-scrollbar": {
      width: 8,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: theme.custom.sidebar,
    },
    "&::-webkit-scrollbar-thumb": {
      minHeight: 40,
      border: `2px solid ${theme.custom.sidebar}`,
      borderRadius: 8,
      backgroundColor: "rgba(148, 163, 184, 0.58)",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "rgba(203, 213, 225, 0.72)",
    },
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: drawerClosedWidth,
    [theme.breakpoints.up("sm")]: {
      width: drawerClosedWidth,
    },
  },
  // Precisa acompanhar exatamente a altura do toolbar acima.
  // Precisa acompanhar exatamente a altura do toolbar.
  appBarSpacer: {
    minHeight: 48,
    [theme.breakpoints.down("xs")]: {
      minHeight: 44,
    },
  },
  content: {
    flex: 1,
    overflow: "auto",
    background: theme.palette.background.default,
    paddingBottom: 0,
    [theme.breakpoints.down("sm")]: {
      paddingBottom: 72,
    },
  },
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  switch: {
    margin: 0,
  },
  iconButton: {
    color: theme.palette.text.primary,
  },
  userPill: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    // Sem borda nem fundo: a barra ja e uma superficie propria, e a
    // moldura extra so acrescentava altura e ruido visual.
    padding: theme.spacing(0, 0.5),
    color: theme.palette.text.primary,
    [theme.breakpoints.down("xs")]: {
      padding: 0,
      gap: theme.spacing(0.25),
    },
  },
  userText: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.1,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  userName: {
    fontSize: "0.75rem",
    fontWeight: 500,
    lineHeight: 1.3,
  },
  userProfile: {
    fontSize: "0.625rem",
    lineHeight: 1.3,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: theme.palette.text.secondary,
  },
  themeSwitchContainer: {
    display: "flex",
    alignItems: "center",
  },
  themeIcon: {
    color: theme.palette.text.secondary,
  },
  statusChip: {
    height: 20,
    color: theme.palette.text.primary,
    fontWeight: 800,
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  passwordField: {
    marginBottom: theme.spacing(2),
  },
  passwordRules: {
    display: "grid",
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.5),
  },
  passwordRule: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  passwordRuleValid: {
    color: theme.palette.text.primary,
  },
  validIcon: {
    color: "#22C55E",
    fontSize: 18,
  },
  invalidIcon: {
    color: theme.palette.text.disabled,
    fontSize: 18,
  },
}));

const statusLabels = {
  online: "Online",
  away: "Ausente",
  offline: "Offline",
};

const statusColors = {
  online: "#22C55E",
  away: "#F59E0B",
  offline: "#94A3B8",
};

const initialPasswordChange = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: ""
};

const getPasswordRules = values => [
  { key: "length", label: "Minimo de 8 caracteres", valid: values.newPassword.length >= 8 },
  { key: "letter", label: "Tem pelo menos uma letra", valid: /[A-Za-z]/.test(values.newPassword) },
  { key: "number", label: "Tem pelo menos um numero", valid: /\d/.test(values.newPassword) },
  { key: "special", label: "Tem caractere especial", valid: /[^A-Za-z0-9]/.test(values.newPassword) },
  { key: "confirm", label: "Confirmacao igual a nova senha", valid: values.confirmPassword.length > 0 && values.newPassword === values.confirmPassword }
];

/*
 * Ultima atividade, compartilhada entre as abas.
 * =============================================================
 * O relogio de inatividade era por aba. Quem trabalhava numa aba e
 * deixava outra aberta e parada era deslogado pela aba parada — e, como o
 * token mora no localStorage, a sessao caia nas duas ao mesmo tempo.
 * Parecia deslogar "do nada" no meio do trabalho.
 *
 * Guardando o instante no localStorage, qualquer aba que registre
 * atividade zera o relogio de todas. Cada aba continua conferindo
 * sozinha; o que passou a ser comum e a resposta para "quando foi a
 * ultima vez que esta pessoa mexeu no sistema?".
 * =============================================================
 */
const CHAVE_ULTIMA_ATIVIDADE = "rsdesk:ultima-atividade";

// Quanto tempo antes do fim o aviso aparece.
const AVISO_ANTES_EM_SEGUNDOS = 60;

const marcarAtividade = () => {
  try {
    localStorage.setItem(CHAVE_ULTIMA_ATIVIDADE, String(Date.now()));
  } catch (err) {
    /* modo restrito: cai para o relogio local da aba */
  }
};

const ultimaAtividade = padrao => {
  try {
    const guardado = Number(localStorage.getItem(CHAVE_ULTIMA_ATIVIDADE));
    // O maior dos dois: se outra aba registrou algo mais recente, vale ela.
    return Number.isFinite(guardado) && guardado > 0 ? Math.max(guardado, padrao) : padrao;
  } catch (err) {
    return padrao;
  }
};

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordChange, setPasswordChange] = useState(initialPasswordChange);
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const { handleLogout, loading, user } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      return stored === null ? document.body.offsetWidth > 600 : stored === "true";
    } catch (err) {
      return document.body.offsetWidth > 600;
    }
  });
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const { darkMode, toggleTheme } = useThemeContext();
  const branding = useBranding();
  const backendUrl = getBackendUrl() || "http://localhost:8085";
  const brandName = branding.brandName || "RSDESK";
  // Logo fixo do sistema: nao ha mais personalizacao por configuracao.
  const sidebarLogo = riosaudeLogo;
  const lastActivityRef = useRef(Date.now());
  const lastTouchRef = useRef(0);
  const returningOnlineRef = useRef(false);
  // Evita disparar o logout mais de uma vez enquanto ele acontece.
  const saindoRef = useRef(false);
  const [inactivitySettings, setInactivitySettings] = useState({});
  // Segundos restantes no aviso de sessao; null enquanto nao ha aviso.
  const [segundosParaSair, setSegundosParaSair] = useState(null);

  useEffect(() => {
    const updateVariant = () => {
      const mobile = window.innerWidth < 960;
      setDrawerVariant(mobile ? "temporary" : "permanent");
      if (mobile) {
        setDrawerOpen(false);
        return;
      }

      try {
        const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
        setDrawerOpen(stored === null ? true : stored === "true");
      } catch (err) {
        setDrawerOpen(true);
      }
    };

    updateVariant();
    window.addEventListener("resize", updateVariant);
    return () => window.removeEventListener("resize", updateVariant);
  }, []);

  useEffect(() => {
    if (drawerVariant !== "permanent") return;
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(drawerOpen));
    } catch (err) {
      // Keep state in memory when storage is unavailable.
    }
  }, [drawerOpen, drawerVariant]);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const { data } = await api.get("/users/inactivity-settings");
        const mapped = {};
        data.forEach(setting => {
          mapped[setting.key] = setting.value;
        });
        setInactivitySettings(mapped);
      } catch (err) {
        // Se falhar, mantem a sessao normal.
      }
    })();
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return undefined;

    // Entrar no sistema conta como atividade: sem isso, uma marca antiga de
    // outra sessao poderia deslogar a pessoa assim que ela abrisse a tela.
    marcarAtividade();

    const recordActivity = async () => {
      lastActivityRef.current = Date.now();
      marcarAtividade();
      if (Date.now() - lastTouchRef.current > 60000) {
        lastTouchRef.current = Date.now();
        api.post("/users/activity").catch(() => {});
      }
      if (user.operationalStatus === "away" && !returningOnlineRef.current) {
        returningOnlineRef.current = true;
        try {
          await api.put(`/users/${user.id}/status`, {
            status: "online",
            reason: "activity_return"
          });
        } finally {
          returningOnlineRef.current = false;
        }
      }
    };

    /*
     * O que conta como atividade.
     *
     * `mousemove` saiu da lista: um mouse tremendo na mesa, ou uma pagina
     * com animacao sob o cursor, mantinha a sessao viva a noite inteira —
     * e a regra deixava de valer justamente no caso que ela existe para
     * cobrir. Ficaram os sinais de uso deliberado.
     */
    const events = ["click", "keydown", "scroll", "touchstart"];
    events.forEach(event => window.addEventListener(event, recordActivity, { passive: true }));

    const interval = setInterval(async () => {
      const appliesToAdmins = inactivitySettings.inactivityAppliesToAdmins === "true";
      if (user.profile === "admin" && !appliesToAdmins) return;

      const paradoDesde = ultimaAtividade(lastActivityRef.current);
      const inactiveMinutes = (Date.now() - paradoDesde) / 60000;
      const autoAwayEnabled = inactivitySettings.autoAwayEnabled === "true";
      const autoLogoutEnabled = inactivitySettings.autoLogoutEnabled === "true";
      const autoAwayMinutes = Number(inactivitySettings.autoAwayMinutes || 0);
      const autoLogoutMinutes = Number(inactivitySettings.autoLogoutMinutes || 0);

      if (
        autoAwayEnabled &&
        autoAwayMinutes > 0 &&
        inactiveMinutes >= autoAwayMinutes &&
        user.operationalStatus === "online"
      ) {
        await api.put(`/users/${user.id}/status`, { status: "away", reason: "auto_away" }).catch(() => {});
      }

      /*
       * Aviso antes de derrubar.
       *
       * Cair sem aviso no meio de um chamado longo e a diferenca entre
       * "o sistema me protegeu" e "o sistema me atrapalhou". No ultimo
       * minuto aparece a contagem com um botao para continuar — que e
       * apenas um registro de atividade, o mesmo do clique.
       */
      const segundosRestantes = Math.round(
        (autoLogoutMinutes * 60000 - (Date.now() - paradoDesde)) / 1000
      );

      if (autoLogoutEnabled && autoLogoutMinutes > 0 && !saindoRef.current) {
        if (segundosRestantes <= 0) {
          saindoRef.current = true;
          setSegundosParaSair(null);
          await api
            .put(`/users/${user.id}/status`, { status: "offline", reason: "auto_logout" })
            .catch(() => {});
          handleLogout();
        } else if (segundosRestantes <= AVISO_ANTES_EM_SEGUNDOS) {
          setSegundosParaSair(segundosRestantes);
        } else {
          setSegundosParaSair(null);
        }
      }
    }, 15000);

    return () => {
      events.forEach(event => window.removeEventListener(event, recordActivity));
      clearInterval(interval);
    };
  }, [user?.id, user?.profile, user?.operationalStatus, inactivitySettings]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenPasswordModal = () => {
    setPasswordChange(initialPasswordChange);
    setPasswordModalOpen(true);
    handleCloseMenu();
  };

  const handleClosePasswordModal = (force = false) => {
    if (passwordSubmitting && !force) return;
    setPasswordModalOpen(false);
    setPasswordChange(initialPasswordChange);
    setShowPasswords(false);
  };

  const handlePasswordChangeInput = event => {
    const { name, value } = event.target;
    setPasswordChange(current => ({
      ...current,
      [name]: value
    }));
  };

  const passwordRules = getPasswordRules(passwordChange);
  const canSubmitPasswordChange = passwordRules.every(rule => rule.valid) && passwordChange.currentPassword.length > 0;

  const handlePasswordChangeSubmit = async event => {
    event.preventDefault();
    if (!canSubmitPasswordChange) return;

    setPasswordSubmitting(true);
    try {
      await api.post("/users/change-password", {
        currentPassword: passwordChange.currentPassword,
        newPassword: passwordChange.newPassword
      });
      toast.success("Senha alterada com sucesso.");
      handleClosePasswordModal(true);
    } catch (err) {
      toastError(err);
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const handleChangeStatus = async status => {
    try {
      await api.put(`/users/${user.id}/status`, { status, reason: "manual" });
      handleCloseMenu();
    } catch (err) {
      toastError(err);
    }
  };

  const drawerClose = () => {
    if (drawerVariant !== "permanent") {
      setDrawerOpen(false);
    }
  };

  const toggleDrawer = () => {
    setDrawerOpen(current => !current);
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
      >
        <div className={clsx(classes.toolbarIcon, !drawerOpen && classes.toolbarIconClosed)}>
          {drawerOpen ? (
            <>
              <div className={classes.sidebarBrand}>
                {sidebarLogo ? (
                  <div className={classes.sidebarLogoFrame}>
                    <img
                      src={sidebarLogo}
                      alt={brandName}
                      className={classes.sidebarLogo}
                      style={branding.brandLogo ? {
                        objectFit: branding.brandLogoFit || "contain",
                        objectPosition: `${branding.brandLogoPositionX || 50}% ${branding.brandLogoPositionY || 50}%`,
                        transform: `scale(${Number(branding.brandLogoScale || 1)})`
                      } : undefined}
                    />
                  </div>
                ) : (
                  <div className={classes.sidebarBrandFallback}>
                    {brandName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <Tooltip title="Recolher menu">
                <IconButton
                  onClick={toggleDrawer}
                  className={classes.collapseButton}
                  aria-label="Recolher menu"
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <div className={classes.sidebarIconFrame}>
              <img src={rsdeskIcon} alt={brandName} className={classes.sidebarIconLogo} />
            </div>
          )}
        </div>
        <List>
          <MainListItems drawerClose={drawerClose} drawerOpen={drawerOpen} />
        </List>
      </Drawer>
      <AppBar
        position="absolute"
        className={clsx(
          classes.appBar,
          drawerVariant === "permanent" && drawerOpen && classes.appBarShift,
          drawerVariant === "permanent" && !drawerOpen && classes.appBarShiftCollapsed,
          drawerVariant !== "permanent" && classes.appBarMobile
        )}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            aria-label="open drawer"
            onClick={toggleDrawer}
            className={clsx(
              classes.menuButton,
              drawerVariant === "permanent" && drawerOpen && classes.menuButtonHidden
            )}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="div"
            variant="h6"
            noWrap
            className={classes.title}
          >
            {drawerVariant !== "permanent" ? brandName : ""}
          </Typography>

          {/*
            size="small" no lugar de transform: scale — o scale encolhe
            o desenho mas mantem a caixa de 38px, e era ela que
            esticava a barra inteira.
          */}
          <div className={classes.themeSwitchContainer}>
            <Brightness4Icon className={classes.themeIcon} />
            <Switch
              size="small"
              checked={darkMode}
              onChange={toggleTheme}
              inputProps={{ "aria-label": "Alternar modo escuro" }}
              color="default"
              className={classes.switch}
            />
          </div>

          {user.id && (
            <NotificationsPopOver className={classes.iconButton} />
          )}

          <div>
            <div className={classes.userPill}>
              <div className={classes.userText}>
                <span className={classes.userName}>{user?.name || "Usuario"}</span>
                <span className={classes.userProfile}>{user?.profile || ""}</span>
              </div>
              <Chip
                size="small"
                className={classes.statusChip}
                label={statusLabels[user?.operationalStatus] || "Offline"}
                style={{ backgroundColor: statusColors[user?.operationalStatus] || statusColors.offline }}
              />
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              className={classes.iconButton}
            >
              <AccountCircle />
            </IconButton>
            </div>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenPasswordModal}>
                Alterar senha
              </MenuItem>
              <MenuItem onClick={() => handleChangeStatus("online")}>
                Status: Online
              </MenuItem>
              <MenuItem onClick={() => handleChangeStatus("away")}>
                Status: Ausente
              </MenuItem>
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <Dialog
        open={passwordModalOpen}
        onClose={handleClosePasswordModal}
        maxWidth="xs"
        fullWidth
      >
        <form onSubmit={handlePasswordChangeSubmit}>
          <DialogTitle>Alterar senha</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              Altere apenas a senha do seu proprio acesso.
            </Typography>
            <TextField
              className={classes.passwordField}
              label="Senha atual"
              name="currentPassword"
              value={passwordChange.currentPassword}
              onChange={handlePasswordChangeInput}
              type={showPasswords ? "text" : "password"}
              variant="outlined"
              fullWidth
              required
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              className={classes.passwordField}
              label="Nova senha"
              name="newPassword"
              value={passwordChange.newPassword}
              onChange={handlePasswordChangeInput}
              type={showPasswords ? "text" : "password"}
              variant="outlined"
              fullWidth
              required
              autoComplete="new-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      type="button"
                      onClick={() => setShowPasswords(current => !current)}
                      aria-label="Mostrar ou ocultar senha"
                    >
                      {showPasswords ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              className={classes.passwordField}
              label="Confirmar nova senha"
              name="confirmPassword"
              value={passwordChange.confirmPassword}
              onChange={handlePasswordChangeInput}
              type={showPasswords ? "text" : "password"}
              variant="outlined"
              fullWidth
              required
              autoComplete="new-password"
            />
            <div className={classes.passwordRules}>
              {passwordRules.map(rule => (
                <div
                  key={rule.key}
                  className={clsx(classes.passwordRule, rule.valid && classes.passwordRuleValid)}
                >
                  {rule.valid ? (
                    <CheckCircle className={classes.validIcon} />
                  ) : (
                    <RadioButtonUnchecked className={classes.invalidIcon} />
                  )}
                  <span>{rule.label}</span>
                </div>
              ))}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePasswordModal} disabled={passwordSubmitting}>
              Cancelar
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={!canSubmitPasswordChange || passwordSubmitting}
            >
              Salvar senha
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ? children : null}
      </main>
      <MobileNavigation />

      {/*
        * Cobranca de ciencia dos avisos obrigatorios.
        *
        * Fica no layout, e nao na tela do Mural, porque a pendencia
        * acompanha a pessoa: ela precisa ser lembrada mesmo enquanto
        * atende. Nao aparece por cima da troca de senha obrigatoria — dois
        * modais empilhados fazem fechar os dois no reflexo.
        */}
      {/*
        * Aviso de sessao prestes a encerrar.
        *
        * "Continuar conectado" nao renova nada por fora: ele registra
        * atividade, exatamente como um clique faria. Assim so existe um
        * conceito de "a pessoa esta aqui", e nao dois caminhos que podem
        * divergir.
        */}
      <Dialog open={segundosParaSair !== null} maxWidth="xs">
        <DialogTitle>Sua sessao vai encerrar</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            O sistema nao registra atividade sua ha algum tempo. Por seguranca, a
            sessao sera encerrada em{" "}
            <strong>{segundosParaSair} segundo{segundosParaSair === 1 ? "" : "s"}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              handleLogout();
            }}
          >
            Sair agora
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              lastActivityRef.current = Date.now();
              marcarAtividade();
              lastTouchRef.current = Date.now();
              api.post("/users/activity").catch(() => {});
              setSegundosParaSair(null);
            }}
          >
            Continuar conectado
          </Button>
        </DialogActions>
      </Dialog>

      <PendingAnnouncementsAlert
        ativo={Boolean(user?.id) && !user?.mustChangePassword}
        bloqueado={passwordModalOpen}
      />
    </div>
  );
};

export default LoggedInLayout;
